import { Trash2 } from 'lucide-react';
import type { Conversation } from '../../types';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

interface GroupedConversations {
  today: Conversation[];
  yesterday: Conversation[];
  lastWeek: Conversation[];
  earlier: Conversation[];
}

function groupConversations(conversations: Conversation[]): GroupedConversations {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);

  return {
    today: conversations.filter((c) => new Date(c.updatedAt) >= today),
    yesterday: conversations.filter((c) => {
      const date = new Date(c.updatedAt);
      return date >= yesterday && date < today;
    }),
    lastWeek: conversations.filter((c) => {
      const date = new Date(c.updatedAt);
      return date >= lastWeek && date < yesterday;
    }),
    earlier: conversations.filter((c) => new Date(c.updatedAt) < lastWeek),
  };
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

function ConversationItem({
  conversation,
  isSelected,
  onSelect,
  onDelete,
}: ConversationItemProps) {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Delete this conversation?')) {
      onDelete(conversation.id);
    }
  };

  return (
    <div
      onClick={() => onSelect(conversation.id)}
      className={`
        w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors
        group flex items-center justify-between gap-2 cursor-pointer
        ${isSelected ? 'bg-gray-100 border-l-2 border-blue-600' : ''}
      `}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{conversation.title}</p>
        <p className="text-xs text-gray-500">
          {conversation.messageCount} messages · {formatRelativeTime(conversation.updatedAt)}
        </p>
      </div>

      <button
        onClick={handleDelete}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
        title="Delete conversation"
      >
        <Trash2 className="h-4 w-4 text-red-500" />
      </button>
    </div>
  );
}

interface ConversationGroupProps {
  title: string;
  conversations: Conversation[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

function ConversationGroup({
  title,
  conversations,
  selectedId,
  onSelect,
  onDelete,
}: ConversationGroupProps) {
  if (conversations.length === 0) return null;

  return (
    <div className="mb-4">
      <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
        {title}
      </h3>
      <div className="space-y-1">
        {conversations.map((conversation) => (
          <ConversationItem
            key={conversation.id}
            conversation={conversation}
            isSelected={conversation.id === selectedId}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  onDelete,
}: ConversationListProps) {
  const grouped = groupConversations(conversations);

  return (
    <div className="py-2">
      <ConversationGroup
        title="Today"
        conversations={grouped.today}
        selectedId={selectedId}
        onSelect={onSelect}
        onDelete={onDelete}
      />
      <ConversationGroup
        title="Yesterday"
        conversations={grouped.yesterday}
        selectedId={selectedId}
        onSelect={onSelect}
        onDelete={onDelete}
      />
      <ConversationGroup
        title="Last 7 Days"
        conversations={grouped.lastWeek}
        selectedId={selectedId}
        onSelect={onSelect}
        onDelete={onDelete}
      />
      <ConversationGroup
        title="Earlier"
        conversations={grouped.earlier}
        selectedId={selectedId}
        onSelect={onSelect}
        onDelete={onDelete}
      />
    </div>
  );
}
